// =============================================================================
// calendar-breaks — pauses in the opening hours, and override reasons, for the
// admin calendar (work item calendar-pauses-override-reasons)
// =============================================================================
// A pause is the gap between two opening periods of the same day, e.g. open
// 13:00–14:20 and 15:00–18:00 leaves 14:20–15:00. The schedule produces no
// slots there, so the calendar used to skip those rows. The calendar now draws
// them as grey, non-bookable "Pauze" cells.
//
// Pauses come from booking.location_schedule_rule(+_period), with the same
// override gate the database readers use (fn_location_has_active_override):
// on a date where an active is_override rule matches, only override rules
// count. Overlapping periods are merged before the gaps are taken.
//
// Override reasons come from booking.schedule_overrides.reason, typed in
// Setup > Overrides. Team outing holds (team_quote_id) are left out: the
// calendar already draws those as team cells.
import { supabase } from './supabase';
import { isoWeekdayOf } from './schedule-rules';
import { rangeDays } from './dates';
import type { Departure } from './departures';

/** Slot step of the schedule (fn_calendar_departures generates every 20 min). */
export const SLOT_STEP_MIN = 20;

export interface ScheduleRuleRow {
  id: string;
  location_id: string;
  day_of_week: number[];
  valid_from: string;
  valid_until: string;
  is_override: boolean;
  periods: Array<{ opens_at: string; closes_at: string }>;
}

export interface CalendarBreak {
  date: string;        // YYYY-MM-DD
  location_id: string;
  start: string;       // HH:MM, the close of the period before
  end: string;         // HH:MM, the open of the period after
}

export function toMinutes(hm: string): number {
  const [h, m] = hm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export function toHM(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

/** Rules that apply to one location on one date, after the override gate. */
export function rulesForDate(rules: ScheduleRuleRow[], locationId: string, date: string): ScheduleRuleRow[] {
  const dow = isoWeekdayOf(date);
  if (dow === null) return [];
  const matching = rules.filter(
    (r) =>
      r.location_id === locationId &&
      r.valid_from <= date &&
      r.valid_until >= date &&
      r.day_of_week.includes(dow),
  );
  const overrides = matching.filter((r) => r.is_override);
  return overrides.length > 0 ? overrides : matching;
}

/** Gaps between the merged opening periods of a day; [] for one period or none. */
export function gapsBetweenPeriods(periods: Array<{ opens_at: string; closes_at: string }>): Array<{ start: number; end: number }> {
  const spans = periods
    .map((p) => ({ start: toMinutes(p.opens_at), end: toMinutes(p.closes_at) }))
    .filter((s) => s.end > s.start)
    .sort((a, b) => a.start - b.start);
  const merged: Array<{ start: number; end: number }> = [];
  for (const s of spans) {
    const last = merged[merged.length - 1];
    if (last && s.start <= last.end) last.end = Math.max(last.end, s.end);
    else merged.push({ ...s });
  }
  const gaps: Array<{ start: number; end: number }> = [];
  for (let i = 1; i < merged.length; i++) {
    gaps.push({ start: merged[i - 1]!.end, end: merged[i]!.start });
  }
  return gaps;
}

/** Every pause per (location, date) in [from, to]. */
export function computeBreaks(
  rules: ScheduleRuleRow[],
  locationIds: Iterable<string>,
  from: string,
  to: string,
): CalendarBreak[] {
  const out: CalendarBreak[] = [];
  const days = rangeDays(from, Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1);
  for (const locationId of locationIds) {
    for (const date of days) {
      const periods = rulesForDate(rules, locationId, date).flatMap((r) => r.periods);
      for (const g of gapsBetweenPeriods(periods)) {
        out.push({ date, location_id: locationId, start: toHM(g.start), end: toHM(g.end) });
      }
    }
  }
  return out;
}

/** Slot times (HH:MM) that fall inside a pause: start, start+20, … while < end. */
export function breakSlotTimes(b: Pick<CalendarBreak, 'start' | 'end'>): string[] {
  const out: string[] = [];
  const end = toMinutes(b.end);
  for (let t = toMinutes(b.start); t < end; t += SLOT_STEP_MIN) out.push(toHM(t));
  return out;
}

/** Pauses keyed by `${date}|${HH:MM}` for each slot time inside them. */
export function breaksByCell(breaks: CalendarBreak[]): Map<string, CalendarBreak[]> {
  const out = new Map<string, CalendarBreak[]>();
  for (const b of breaks) {
    for (const t of breakSlotTimes(b)) {
      const k = `${b.date}|${t}`;
      const list = out.get(k);
      if (list) list.push(b);
      else out.set(k, [b]);
    }
  }
  return out;
}

export async function fetchScheduleRules(locationId: string | null): Promise<ScheduleRuleRow[]> {
  let q = supabase
    .schema('booking')
    .from('location_schedule_rule')
    .select('id, location_id, day_of_week, valid_from, valid_until, is_override, periods:location_schedule_rule_period(opens_at, closes_at)')
    .eq('is_active', true);
  if (locationId) q = q.eq('location_id', locationId);
  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as Array<ScheduleRuleRow & { periods: ScheduleRuleRow['periods'] | null }>).map((r) => ({
    ...r,
    periods: (r.periods ?? []).map((p) => ({ opens_at: p.opens_at.slice(0, 5), closes_at: p.closes_at.slice(0, 5) })),
  }));
}

// ---- Override notes ---------------------------------------------------------

export interface CalendarOverrideRow {
  room_id: string;
  slot_start: string;
  experience_id: string | null;
  kind: 'blocked' | 'reduced_capacity';
  override_capacity: number | null;
  reason: string | null;
}

export interface OverrideNote {
  kind: 'blocked' | 'reduced_capacity';
  reason: string | null;
  room_name: string;
  experience_name: string | null;   // null = whole room
  override_capacity: number | null;
}

export async function fetchCalendarOverrides(fromDate: string, toDate: string): Promise<CalendarOverrideRow[]> {
  // Venue days are Europe/Amsterdam; a one-day margin each side covers the
  // UTC offset, and notesByCell only keeps slots the calendar actually has.
  const fromIso = new Date(Date.parse(`${fromDate}T00:00:00Z`) - 86_400_000).toISOString();
  const toIso = new Date(Date.parse(`${toDate}T00:00:00Z`) + 2 * 86_400_000).toISOString();
  const { data, error } = await supabase
    .schema('booking')
    .from('schedule_overrides')
    .select('room_id, slot_start, experience_id, kind, override_capacity, reason')
    .is('team_quote_id', null)
    .gte('slot_start', fromIso)
    .lt('slot_start', toIso);
  if (error) throw error;
  return (data ?? []) as CalendarOverrideRow[];
}

/**
 * Overrides keyed by `${date}|${HH:MM}`, matched to the calendar's departures
 * by (room, slot instant). Overrides on slots the calendar does not show (other
 * location, outside the range) drop out.
 */
export function notesByCell(overrides: CalendarOverrideRow[], departures: Departure[]): Map<string, OverrideNote[]> {
  const bySlotRoom = new Map<string, Departure[]>();
  for (const d of departures) {
    const k = `${Date.parse(d.slot_start)}|${d.room_id}`;
    const list = bySlotRoom.get(k);
    if (list) list.push(d);
    else bySlotRoom.set(k, [d]);
  }
  const out = new Map<string, OverrideNote[]>();
  for (const o of overrides) {
    const deps = bySlotRoom.get(`${Date.parse(o.slot_start)}|${o.room_id}`);
    if (!deps || deps.length === 0) continue;
    const first = deps[0]!;
    const exp = o.experience_id ? deps.find((d) => d.experience_id === o.experience_id) : null;
    if (o.experience_id && !exp) continue;
    const note: OverrideNote = {
      kind: o.kind,
      reason: o.reason?.trim() || null,
      room_name: first.room_name,
      experience_name: exp ? exp.experience_name : null,
      override_capacity: o.override_capacity,
    };
    const k = `${first.date}|${first.time_of_day}`;
    const list = out.get(k);
    if (list) list.push(note);
    else out.set(k, [note]);
  }
  return out;
}

/**
 * One-line label for an override in a calendar cell. `withRoom` names the room
 * when the slot has more than one, so a note under an open room is not read as
 * "the whole slot is closed".
 */
export function overrideNoteLabel(n: OverrideNote, withRoom = false): string {
  const room = withRoom ? `${n.room_name} ` : '';
  const what =
    n.kind === 'blocked'
      ? n.experience_name
        ? `${room}${n.experience_name} dicht`
        : withRoom ? `${n.room_name} gesloten` : 'Gesloten'
      : `${room}max ${n.override_capacity ?? 0}`.replace(/^max/, 'Max');
  return n.reason ? `${what} · ${n.reason}` : what;
}
